import { TextField } from '@mui/material'
import { DesktopDatePicker, LocalizationProvider, PickersDay, type PickersDayProps } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import { useState } from 'react'

export interface WeekPickerProps {
  value: string
  onChange: (weekStart: string) => void
  selectableWeekStarts?: string[]
  minSelectableDate?: string
  maxSelectableDate?: string
  label?: string
  disabled?: boolean
}

const attendanceFontFamily = 'Quicksand, sans-serif'

export function WeekPicker (props: WeekPickerProps): JSX.Element {
  const [hoveredWeekStart, setHoveredWeekStart] = useState<string | null>(null)
  const selectedWeekStart = weekStartDateValue(props.value)
  const selectableWeekKeys = props.selectableWeekStarts?.map(weekStartDateValue)
  const minSelectableDate = props.minSelectableDate == null ? undefined : dateKey(props.minSelectableDate)
  const maxSelectableDate = props.maxSelectableDate == null ? undefined : dateKey(props.maxSelectableDate)

  const handleChange = (newValue: Dayjs | null): void => {
    if (newValue == null) return
    const nextDate = toDateValue(new Date(newValue.year(), newValue.month(), newValue.date()))
    if (minSelectableDate != null && nextDate < minSelectableDate) return
    if (maxSelectableDate != null && nextDate > maxSelectableDate) return
    const nextWeekStart = weekStartDateValue(nextDate)
    if (selectableWeekKeys != null && !selectableWeekKeys.includes(nextWeekStart)) return
    props.onChange(nextWeekStart)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        disabled={props.disabled}
        label={props.label}
        value={dayjs(selectedWeekStart)}
        onChange={handleChange}
        shouldDisableDate={(day) => {
          const dayKey = toDateValueFromDayjs(day)
          if (!isWeekday(dayKey)) return true
          if (minSelectableDate != null && dayKey < minSelectableDate) return true
          if (maxSelectableDate != null && dayKey > maxSelectableDate) return true
          return selectableWeekKeys != null && !selectableWeekKeys.includes(weekStartDateValue(dayKey))
        }}
        renderDay={(day, _selectedDays, pickersDayProps) => renderWeekDay({
          day,
          hoveredWeekStart,
          minSelectableDate,
          maxSelectableDate,
          pickersDayProps,
          selectableWeekKeys,
          selectedWeekStart,
          setHoveredWeekStart
        })}
        renderInput={(inputProps) => (
          <TextField
            {...inputProps}
            size="small"
            inputProps={{
              ...inputProps.inputProps,
              value: weekRangeLabel(selectedWeekStart)
            }}
            sx={{
              minWidth: 220,
              '& .MuiInputBase-root': {
                borderRadius: '8px',
                color: '#595959',
                fontFamily: attendanceFontFamily,
                fontSize: '0.875rem',
                fontWeight: 500
              },
              '& .MuiInputLabel-root': {
                color: '#8F8F8F',
                fontFamily: attendanceFontFamily,
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        )}
      />
    </LocalizationProvider>
  )
}

function renderWeekDay (props: {
  day: Dayjs
  hoveredWeekStart: string | null
  minSelectableDate: string | undefined
  maxSelectableDate: string | undefined
  pickersDayProps: PickersDayProps<Dayjs>
  selectableWeekKeys: string[] | undefined
  selectedWeekStart: string
  setHoveredWeekStart: (weekStart: string | null) => void
}): JSX.Element {
  const { day, hoveredWeekStart, minSelectableDate, maxSelectableDate, pickersDayProps, selectableWeekKeys, selectedWeekStart, setHoveredWeekStart } = props
  const dayKey = toDateValueFromDayjs(day)
  const weekStart = weekStartDateValue(dayKey)
  const selectable = isWeekday(dayKey) &&
    (minSelectableDate == null || dayKey >= minSelectableDate) &&
    (maxSelectableDate == null || dayKey <= maxSelectableDate) &&
    (selectableWeekKeys == null || selectableWeekKeys.includes(weekStart))
  const isSelectedWeek = selectable && weekStart === selectedWeekStart
  const isHoveredWeek = selectable && weekStart === hoveredWeekStart && weekStart !== selectedWeekStart
  const isHighlightedWeek = isSelectedWeek || isHoveredWeek
  const isFirst = dayKey === weekStart
  const isLast = dayKey === weekFridayDateValue(weekStart)

  return (
    <PickersDay
      {...pickersDayProps}
      disableMargin
      onMouseEnter={(event) => {
        pickersDayProps.onMouseEnter?.(event)
        if (selectable) setHoveredWeekStart(weekStart)
      }}
      onMouseLeave={(event) => {
        pickersDayProps.onMouseLeave?.(event)
        setHoveredWeekStart(null)
      }}
      sx={{
        borderRadius: isHighlightedWeek
          ? isFirst ? '18px 0 0 18px' : isLast ? '0 18px 18px 0' : 0
          : undefined,
        color: isSelectedWeek ? '#7136BC' : isHoveredWeek ? '#595959' : undefined,
        fontFamily: attendanceFontFamily,
        fontWeight: isSelectedWeek ? 600 : 500,
        mx: 0,
        ...(isSelectedWeek
          ? {
              backgroundColor: '#F1EBF9',
              '&:hover, &:focus': {
                backgroundColor: '#E6DAF4'
              }
            }
          : {}),
        ...(isHoveredWeek
          ? {
              backgroundColor: '#F8F4FD',
              '&:hover, &:focus': {
                backgroundColor: '#F1EBF9'
              }
            }
          : {})
      }}
    />
  )
}

export function weekStartDateValue (value: string): string {
  const date = parseDateOnly(value)
  const diff = (date.getDay() + 6) % 7
  date.setDate(date.getDate() - diff)
  return toDateValue(date)
}

function dateKey (value: string): string {
  return value.substring(0, 10)
}

function weekFridayDateValue (weekStart: string): string {
  const date = parseDateOnly(weekStart)
  date.setDate(date.getDate() + 4)
  return toDateValue(date)
}

function weekRangeLabel (weekStart: string): string {
  const start = parseDateOnly(weekStart)
  const end = parseDateOnly(weekStart)
  end.setDate(start.getDate() + 4)
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

function parseDateOnly (value: string): Date {
  const [year, month, day] = value.substring(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toDateValue (date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function toDateValueFromDayjs (date: Dayjs): string {
  const year = date.year()
  const month = String(date.month() + 1).padStart(2, '0')
  const day = String(date.date()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function isWeekday (value: string): boolean {
  const day = parseDateOnly(value).getDay()
  return day >= 1 && day <= 5
}
